"use client"

import React, { useState, useEffect, useRef, useCallback } from "react";
import { getCachedVideoUrl } from "@/utils/videoUrlCache";
import { videoPreloadManager } from "@/utils/videoPerformance";

interface VideoState {
  isLoaded: boolean;
  isMuted: boolean;
  loadError: boolean;
  isVisible: boolean;
  showVideo: boolean;
}

// 音频控制按钮组件
const AudioControlButton: React.FC<{
  isMuted: boolean;
  onToggleMute: () => void;
}> = ({ isMuted, onToggleMute }) => {
  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleMute();
  };

  return (
    <button
      onClick={handleClick}
      className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 hover:bg-black/70 transition-all duration-200 group"
      aria-label={isMuted ? "Unmute video" : "Mute video"}
    >
      {isMuted ? (
        <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white group-hover:text-green-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
        </svg>
      ) : (
        <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white group-hover:text-green-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      )}
    </button>
  );
};

AudioControlButton.displayName = 'AudioControlButton';

// 简化的视频组件
const OptimizedVideo = React.forwardRef<HTMLVideoElement, {
  src: string;
  className?: string;
}>(({ src, className = '' }, ref) => {
  const [videoState, setVideoState] = useState<VideoState>({
    isLoaded: false,
    isMuted: true,
    loadError: false,
    isVisible: false,
    showVideo: false
  });

  const [isClient, setIsClient] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 客户端检测
  useEffect(() => {
    setIsClient(true);
  }, []);

  // 简化的懒加载逻辑
  useEffect(() => {
    if (!isClient || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVideoState(prev => ({ 
            ...prev, 
            isVisible: true,
            showVideo: true
          }));
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isClient]);

  // 处理视频加载数据
  const handleLoadedData = useCallback(() => {
    console.log('Video loaded successfully');
    setVideoState(prev => ({ ...prev, isLoaded: true, loadError: false }));
    
    // 视频加载完成后自动播放
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // 如果自动播放失败，静音后重试
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {
            console.log('Video autoplay failed');
          });
        }
      });
    }
  }, []);

  // 处理视频错误
  const handleError = useCallback(() => {
    console.log('Video load error');
    setVideoState(prev => ({ ...prev, loadError: true, isLoaded: false }));
  }, []);

  // 处理静音切换
  const handleToggleMute = useCallback(() => {
    if (videoRef.current) {
      const newMutedState = !videoState.isMuted;
      videoRef.current.muted = newMutedState;
      setVideoState(prev => ({ ...prev, isMuted: newMutedState }));
      
      // 如果取消静音且视频已加载，尝试播放
      if (!newMutedState && videoState.isLoaded) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [videoState.isMuted, videoState.isLoaded]);

  // 视频预加载管理
  useEffect(() => {
    if (videoState.showVideo) {
      videoPreloadManager.preloadVideo(src);
    }
  }, [videoState.showVideo, src]);

  // 调试信息
  useEffect(() => {
    console.log('Video state:', {
      isClient,
      showVideo: videoState.showVideo,
      isLoaded: videoState.isLoaded,
      loadError: videoState.loadError
    });
  }, [isClient, videoState.showVideo, videoState.isLoaded, videoState.loadError]);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      {/* 加载动画 - 只在视频未加载完成时显示 */}
      {isClient && videoState.showVideo && !videoState.isLoaded && !videoState.loadError && (
        <div className="absolute inset-0 bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center z-20">
          <div className="text-center text-gray-400 px-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 mx-auto mb-2 sm:mb-3 lg:mb-4 border-4 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
            <p className="text-sm sm:text-base lg:text-lg font-semibold mb-1 sm:mb-2">AI Video Loading</p>
            <p className="text-xs sm:text-sm text-gray-200">Loading...</p>
          </div>
        </div>
      )}

      {/* 视频元素 */}
      {isClient && videoState.showVideo && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover ${className} transition-opacity duration-500 z-10 ${
            videoState.isLoaded && !videoState.loadError ? 'opacity-100' : 'opacity-0'
          }`}
          autoPlay={true}
          loop
          muted={videoState.isMuted}
          playsInline
          preload="metadata"
          controls={false}
          disablePictureInPicture={true}
          disableRemotePlayback={true}
          aria-label="AI Video Demo"
          aria-describedby="video-description"
          onLoadedData={handleLoadedData}
          onError={handleError}
        >
          <source src={src} type="video/mp4" />
          {/* 无障碍回退信息 */}
          <div id="video-description" className="sr-only">
            This is an AI-generated video demo, showcasing the effect of AI video generation technology.
          </div>
          Your browser does not support the video tag.
        </video>
      )}

      {/* 音频控制按钮 */}
      {isClient && videoState.showVideo && videoState.isLoaded && (
        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 lg:top-3 lg:right-3 z-50 pointer-events-auto">
          <AudioControlButton
            isMuted={videoState.isMuted}
            onToggleMute={handleToggleMute}
          />
        </div>
      )}

      {/* 视频边框 */}
      <div className="absolute inset-0 rounded-2xl border border-green-400/30 pointer-events-none" />
    </div>
  )
});

OptimizedVideo.displayName = 'OptimizedVideo';

export default OptimizedVideo; 